// src/postplans/pages/PostPlanAssistPage.jsx
import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Switch } from "@mui/material";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Stack,
  LinearProgress,
} from "@mui/material";

function guessNextVersionLabel(input = "") {
  const s = String(input || "").trim();
  if (!s) return "v1";
  const m = s.match(/\bv(\d+)\b/i);
  if (m?.[1]) return `v${Number(m[1]) + 1}`;
  return "v1";
}

function toIT(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT");
}

export default function PostPlanAssistPage() {
  const { id } = useParams(); // production id
  const navigate = useNavigate();

  // Conversational state
  const steps = ["Base", "Calendario", "Output"];
  const [activeStep, setActiveStep] = useState(0);
    const [chatInput, setChatInput] = useState("");
    const [chat, setChat] = useState([
    { role: "assistant", text: "Dimmi che piano vuoi creare e ti guido io." }
    ]);
    const chatEndRef = useRef(null);
const [aiLoading, setAiLoading] = useState(false);
useEffect(() => {
  chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
}, [chat, aiLoading]);
    const [deep, setDeep] = useState(false);    
    const [answeredKeys, setAnsweredKeys] = useState(new Set());
  // Input
  const [versionLabel, setVersionLabel] = useState("");
  const [postStart, setPostStart] = useState("");
  const [deliveryDeadline, setDeliveryDeadline] = useState("");
  const [txDate, setTxDate] = useState("");
  const [episodeCount, setEpisodeCount] = useState("");
  const [cadence, setCadence] = useState("weekly");
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]); // Lun-Ven default
  const [allowSaturday, setAllowSaturday] = useState(false);
  const [proposal, setProposal] = useState(null); // { safe, fast } oppure null
  const [selectedVariant, setSelectedVariant] = useState(null); // "safe" | "fast" | null
  const [selectedDraft, setSelectedDraft] = useState(null);     // proposal.safe/fast
  const [proposalLoading, setProposalLoading] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
// RESET PROPOSTA SE CAMBI PARAMETRI CORE
useEffect(() => {
  if (proposal) {
    setProposal(null);
    setSelectedDraft(null);
    setSelectedVariant(null);
    setMissingFields([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [postStart, deliveryDeadline, txDate, episodeCount, cadence, allowSaturday]);
  // UI-only
  const isValid = useMemo(() => {
    return (
      String(versionLabel || "").trim() &&
      postStart &&
      deliveryDeadline &&
      Number(episodeCount) > 0
    );
  }, [versionLabel, postStart, deliveryDeadline, episodeCount]);

  const progress = useMemo(() => {
    // 0..100 based on filled fields
    const total = 5;
    let done = 0;
    if (String(versionLabel || "").trim()) done++;
    if (postStart) done++;
    if (deliveryDeadline) done++;
    if (Number(episodeCount) > 0) done++;
    if (cadence) done++;
    return Math.round((done / total) * 100);
  }, [versionLabel, postStart, deliveryDeadline, episodeCount, cadence]);

  const suggestions = useMemo(() => {
    const ep = Number(episodeCount) || 0;
    const cad = cadence;

    // super semplice: propone “linee” indicative
    const base = [
      { name: "Ingest/Sync", turns: 1, lanes: cad.startsWith("daily") ? 2 : 1 },
      { name: "Montaggio", turns: cad === "weekly" ? 5 : 3, lanes: cad.startsWith("daily") ? 3 : 2 },
      { name: "Visioni", turns: 2, lanes: cad.startsWith("daily") ? 2 : 1 },
      { name: "Finalizzazione", turns: 2, lanes: cad.startsWith("daily") ? 2 : 1 },
    ];

    const notes = [];
    if (cad === "daily_7") notes.push("Daily 7/7: prevedi almeno 2 linee su Ingest e 3 su Montaggio.");
    if (cad === "daily_5") notes.push("Daily 5/7: weekend libero; utile buffer naturale.");
    if (cad === "weekly") notes.push("Settimanale: 2 linee su Montaggio è spesso sufficiente.");

    if (ep >= 30 && cad.startsWith("daily")) notes.push("Stagione lunga: valuta una linea extra su Finalizzazione.");
    if (!deliveryDeadline) notes.push("Serve la delivery: è la vera deadline operativa.");

    return { phases: base, notes };
  }, [episodeCount, cadence, deliveryDeadline]);

  const workingDaysLabel = useMemo(() => {
    const map = { 1: "Lun", 2: "Mar", 3: "Mer", 4: "Gio", 5: "Ven", 6: "Sab", 0: "Dom" };
    return (workingDays || []).map((d) => map[d]).join(" • ") || "—";
  }, [workingDays]);

  const toggleDay = (d) => {
    setWorkingDays((prev) => {
      const set = new Set(prev);
      if (set.has(d)) set.delete(d);
      else set.add(d);
      // mantieni ordinamento coerente (Lun..Dom)
      const order = [1, 2, 3, 4, 5, 6, 0];
      return order.filter((x) => set.has(x));
    });
  };
const formatHeadcount = (role, headcount) => {
  const r = String(role || "");
  const raw = Number(headcount);

  // se vuoi consentire part-time SOLO per Audio/Color, metti true qui
  const allowFTE = false; // <-- mettilo true solo se vuoi mostrare "0.5 FTE"

  let hc = Number.isFinite(raw) ? Math.max(0, raw) : 0;

  if (allowFTE && (r === "Audio" || r === "Color")) {
    // mostriamo part-time come FTE, NON come "persone"
    hc = Math.round(hc * 2) / 2;
    const label = hc === 0 ? "non previsto" : `${hc} FTE`;
    return { hc, label };
  }

  // default: persone = interi (niente 1.5)
  hc = Math.ceil(hc); // 1.5 -> 2, 1.1 -> 2
  const label = hc === 0 ? "non previsto" : `${hc} ${hc === 1 ? "persona" : "persone"}`;
  return { hc, label };
};
  const handleNext = () => setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));
  const handleApply = async () => {
  if (!isValid) return;

  console.log("[ASSIST][DRAFT]", {
    productionId: Number(id),
    versionLabel,
    postStart,
    deliveryDeadline,
    txDate: txDate || null,
    episodeCount: Number(episodeCount),
    cadence,
    workingDays,
    allowSaturday,
  });

  alert("✅ Draft pronto (non ancora collegato al backend generatore).");
};
const generateProposal = async () => {
  setProposalLoading(true);
  setMissingFields([]);

  const context = {
    productionId: Number(id),
    fields: {
      versionLabel,
      postStart,
      deliveryDeadline,
      txDate: txDate || null,
      episodeCount,
      cadence,
      workingDays,
      allowSaturday,
    },
    answeredKeys: Array.from(answeredKeys),
  };

  try {
    const resp = await fetch("http://localhost:3001/api/ai/postplan-assist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: "GENERA 2 PROPOSTE: SAFE e FAST con staffing stimato per ruolo.",
        context,
        deep,
      }),
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

    if (Array.isArray(data?.missing_fields) && data.missing_fields.length) {
      setMissingFields(data.missing_fields);
      setProposal(null);
      // messaggio UX pulito
      setChat((c) => [...c, { role: "assistant", text: data?.assistant_message || "Compila i campi mancanti sopra." }]);
      return;
    }

    if (data?.proposal) {
      setProposal(data.proposal);
      setChat((c) => [...c, { role: "assistant", text: data?.assistant_message || "Ho generato le proposte SAFE/FAST." }]);
    } else if (data?.assistant_message) {
      setChat((c) => [...c, { role: "assistant", text: data.assistant_message }]);
    }
  } catch (e) {
    setChat((c) => [...c, { role: "assistant", text: "Errore AI: " + (e?.message || e) }]);
  } finally {
    setProposalLoading(false);
  }
};
function parseQuickFieldsFromText(text) {
  const s = String(text || "").toLowerCase();

  // episodi/puntate: "30 puntate", "8 episodi"
  const mEp = s.match(/(\d+)\s*(puntate|episodi|ep)\b/);
  const episodeCount = mEp ? Number(mEp[1]) : null;

  // cadenza
  let cadence = null;
  if (/\b(quotidiano|giornalier[oa]|daily)\b/.test(s)) {
    cadence = "daily_7";
    // se scrive 5/7 o lun-ven
    if (/\b(5\/7|lun(edì)?\s*-\s*ven(erdì)?|luned[iì]\s*al\s*venerd[iì])\b/.test(s)) {
      cadence = "daily_5";
    }
  }
  if (/\b(settimanale|weekly)\b/.test(s)) cadence = "weekly";

  return { episodeCount, cadence };
}
const sendToAI = async () => {
  const msg = String(chatInput || "").trim();
  if (!msg || aiLoading) return;

  // parse immediato UI
  const quick = parseQuickFieldsFromText(msg);
  if (quick.episodeCount) setEpisodeCount(String(quick.episodeCount));
  if (quick.cadence) setCadence(quick.cadence);

  const pendingId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  setChatInput("");
  setAiLoading(true);

  // 1) append user + pending assistant in UN solo setState
  setChat((c) => [
    ...c,
    { role: "user", text: msg },
    { role: "assistant", text: "Sto pensando…", pending: true, id: pendingId },
  ]);

  const context = {
    productionId: Number(id),
    fields: { versionLabel, postStart, deliveryDeadline, txDate, episodeCount, cadence, workingDays, allowSaturday },
  };

  try {
    const resp = await fetch("http://localhost:3001/api/ai/postplan-assist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: msg, context, deep }),
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);

    // patch campi (solo se presenti)
    const p = data?.patch || {};
    if (p.versionLabel != null) setVersionLabel(p.versionLabel);
    if (p.postStart != null) setPostStart(p.postStart);
    if (p.deliveryDeadline != null) setDeliveryDeadline(p.deliveryDeadline);
    if (p.txDate === null) setTxDate("");
    if (p.txDate != null) setTxDate(p.txDate);
    if (p.episodeCount != null) setEpisodeCount(String(p.episodeCount));
    if (p.cadence != null) setCadence(p.cadence);
    if (Array.isArray(p.workingDays)) setWorkingDays(p.workingDays);
    if (p.allowSaturday != null) setAllowSaturday(!!p.allowSaturday);

    // 2) sostituisci pending con risposta (niente next_questions)
    const text =
      data?.assistant_message ||
      (data?.warning === "non_json" ? "Risposta non in JSON: controlla prompt/LLM." : "Ok.");

    setChat((c) =>
      c.map((m) => (m.id === pendingId ? { role: "assistant", text } : m))
    );
  } catch (e) {
    setChat((c) =>
      c.map((m) =>
        m.id === pendingId ? { role: "assistant", text: "Errore AI: " + (e?.message || e) } : m
      )
    );
  } finally {
    setAiLoading(false);
  }
};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack spacing={1.25} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Piano Assistito
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Produzione #{id} • Draft conversazionale → preview → apply
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`Completezza: ${progress}%`} variant="outlined" />
            <Button variant="outlined" onClick={() => navigate(`/productions/${id}`)}>
              Torna alla produzione
            </Button>
          </Stack>
        </Stack>

        <LinearProgress variant="determinate" value={progress} />
      </Stack>

      <Grid container spacing={3}>
        {/* Left: Conversational */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              Setup guidato
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Assistente</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption">Deep</Typography>
                    <Switch checked={deep} onChange={(e) => setDeep(e.target.checked)} />
                    </Stack>
                </Stack>

                <Box
  sx={{
    maxHeight: 200,
    overflow: "auto",
    display: "grid",
    gap: 1
  }}
>
  {chat.map((m, idx) => (
    <Box
      key={idx}
      sx={{ p: 1, border: "1px solid #ddd", borderRadius: 2 }}
    >
      <Typography variant="caption">{m.role}</Typography>
      <Typography variant="body2">{m.text}</Typography>
    </Box>
  ))}

  {/* 👇 QUESTO VA QUI */}
  <div ref={chatEndRef} />
</Box>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                    value={chatInput}
                    onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!aiLoading) sendToAI();
                    }
                    }}
                    onChange={(e) => setChatInput(e.target.value)}
                    disabled={aiLoading}
                    fullWidth
                    placeholder="Scrivi qui..."
                    />
                    <Button variant="contained" onClick={sendToAI} disabled={aiLoading}>
                    {aiLoading ? "…" : "Invia"}
                    </Button>                   
                </Stack>
                </Paper>
            {/* Step content */}
            {activeStep === 0 && (
              <Box sx={{ display: "grid", gap: 2 }}>
                <Alert severity="info">
                  Partiamo dai minimi. La <b>delivery</b> è la deadline vera; la <b>TX</b> è informativa.
                </Alert>

                <TextField
                  label="Versione piano (es. v1, v2, 'taglio estivo')"
                  value={versionLabel}
                  onChange={(e) => setVersionLabel(e.target.value)}
                  placeholder={guessNextVersionLabel(versionLabel)}
                  fullWidth
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Numero episodi"
                    type="number"
                    value={episodeCount}
                    onChange={(e) => setEpisodeCount(e.target.value)}
                    fullWidth
                  />

                  <TextField
                    label="Cadenza"
                    select
                    SelectProps={{ native: true }}
                    value={cadence}
                    onChange={(e) => setCadence(e.target.value)}
                    fullWidth
                  >
                    <option value="weekly">Settimanale</option>
                    <option value="daily_5">Daily (5/7)</option>
                    <option value="daily_7">Daily (7/7)</option>
                  </TextField>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between">
                  <span />
                  <Button variant="contained" onClick={handleNext}>
                    Avanti
                  </Button>
                </Stack>
              </Box>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: "grid", gap: 2 }}>
                <Alert severity="warning">
                  Metti date reali: <b>Inizio post</b> e <b>Delivery deadline</b> sono obbligatorie.
                </Alert>

                <TextField
                  label="Inizio Post"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={postStart}
                  onChange={(e) => setPostStart(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Delivery Deadline"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={deliveryDeadline}
                  onChange={(e) => setDeliveryDeadline(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="TX (opzionale)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={txDate}
                  onChange={(e) => setTxDate(e.target.value)}
                  fullWidth
                />

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Giorni lavorativi
                </Typography>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {[
                    { k: 1, t: "Lun" },
                    { k: 2, t: "Mar" },
                    { k: 3, t: "Mer" },
                    { k: 4, t: "Gio" },
                    { k: 5, t: "Ven" },
                    { k: 6, t: "Sab" },
                    { k: 0, t: "Dom" },
                  ].map((d) => {
                    const on = (workingDays || []).includes(d.k);
                    return (
                      <Chip
                        key={d.k}
                        label={d.t}
                        clickable
                        color={on ? "primary" : "default"}
                        variant={on ? "filled" : "outlined"}
                        onClick={() => toggleDay(d.k)}
                      />
                    );
                  })}
                </Stack>

                <Alert severity="info">
                  Attivi: <b>{workingDaysLabel}</b>
                </Alert>

                <Stack direction="row" justifyContent="space-between">
                  <Button onClick={handleBack}>Indietro</Button>
                  <Button variant="contained" onClick={handleNext}>
                    Avanti
                  </Button>
                </Stack>
              </Box>
            )}

            {activeStep === 2 && (
              <Box sx={{ display: "grid", gap: 2 }}>
                <Alert severity={isValid ? "success" : "info"}>
                  {isValid ? (
                    <>
                      Draft pronto. Premi <b>Applica</b> per generare il piano (quando colleghiamo il backend).
                    </>
                  ) : (
                    <>Completa i campi minimi per sbloccare “Applica”.</>
                  )}
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
      AI Plan Designer — SAFE / FAST
    </Typography>
    <Button
      variant="contained"
      onClick={generateProposal}
      disabled={!isValid || proposalLoading}
    >
      {proposalLoading ? "Genero…" : "Genera proposta"}
    </Button>
  </Stack>

  {missingFields?.length ? (
    <Alert severity="warning" sx={{ mt: 1 }}>
      Mancano: <b>{missingFields.join(", ")}</b>
    </Alert>
  ) : null}

  {!proposal ? (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Compila i campi minimi e premi <b>Genera proposta</b>. Otterrai 2 opzioni con fasi+linee, staffing stimato e rischi.
    </Typography>
  ) : (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {["safe", "fast"].map((k) => {
        const title = k === "safe" ? "SAFE (più buffer, meno rischio)" : "FAST (più compressa, più rischio)";
        const data = proposal?.[k];

        return (
          <Grid item xs={12} md={6} key={k}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                {title}
              </Typography>

              {/* Phases */}
              <Stack spacing={0.75}>
                {(data?.phases || []).map((ph) => (
                  <Box key={ph.name} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {ph.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ph.turns} turni • {ph.lanes} linee
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 1.25 }} />
              
              {/* Staffing */}
              <Typography variant="caption" color="text.secondary">
                Staffing stimato
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {(data?.staffing || []).map((s, idx) => {
                  const { hc, label } = formatHeadcount(s?.role, s?.headcount);

                  return (
                    <Typography key={idx} variant="body2">
                      <b>{s?.role}</b>: {label}
                      {s?.notes ? <span style={{ color: "rgba(0,0,0,0.6)" }}> — {s.notes}</span> : null}
                    </Typography>
                  );
                })}
              </Stack>

              {/* Risks */}
              {Array.isArray(data?.risks) && data.risks.length ? (
                <>
                  <Divider sx={{ my: 1.25 }} />
                  <Typography variant="caption" color="text.secondary">
                    Rischi principali
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  {(data?.staffing || []).map((s, idx) => {
                    const { hc, label } = formatHeadcount(s?.role, s?.headcount);

                    const hcLabel =
                      hc === 0
                        ? "non previsto"
                        : `${hc} ${hc === 1 ? "persona" : "persone"}`;

                    return (
                      <Typography key={idx} variant="body2">
                        <b>{s.role}</b>: {hcLabel}
                        {s.notes ? (
                          <span style={{ color: "rgba(0,0,0,0.6)" }}>
                            {" "}
                            — {s.notes}
                          </span>
                        ) : null}
                      </Typography>
                    );
                  })}
                </Stack>
                </>
              ) : null}

              <Divider sx={{ my: 1.25 }} />

              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                setSelectedVariant(k);
                setSelectedDraft(data);
                setChat((c) => [...c, { role: "assistant", text: `Ok: selezionata proposta ${k.toUpperCase()}. Ora puoi generare il piano.` }]);

                // scroll immediato alla sezione "Genera piano" (vedi punto sotto)
                setTimeout(() => {
                    document.getElementById("assist-create-plan")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 50);
                }}
              >
                Applica {k.toUpperCase()}
              </Button>
            </Paper>
            <Box id="assist-create-plan" sx={{ mt: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Piano pronto da generare
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    {selectedVariant
                        ? `Selezionata: ${selectedVariant.toUpperCase()}`
                        : "Seleziona SAFE o FAST per abilitare la generazione."}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    disabled={!selectedDraft}
                    onClick={handleApply}
                >
                    Genera piano
                </Button>
                </Stack>
            </Paper>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  )}
</Paper>

                {suggestions.notes?.length ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Note
                    </Typography>
                    <Stack spacing={0.75}>
                      {suggestions.notes.map((n, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          • {n}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}

                <Stack direction="row" justifyContent="space-between">
                  <Button onClick={handleBack}>Indietro</Button>
                  <Button variant="contained" disabled={!isValid} onClick={handleApply}>
                    Applica e genera piano
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: Preview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, position: "sticky", top: 16 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              Draft leggibile
            </Typography>

            {!isValid ? (
              <Alert severity="info">
                Compila almeno: <b>versione</b>, <b>inizio post</b>, <b>delivery</b>, <b>episodi</b>.
              </Alert>
            ) : (
              <Box sx={{ display: "grid", gap: 1.25 }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`Versione: ${versionLabel}`} />
                  <Chip label={`Episodi: ${episodeCount}`} variant="outlined" />
                  <Chip label={`Cadenza: ${cadence}`} variant="outlined" />
                </Box>

                <Divider />

                <Typography variant="body2">
                  <b>Inizio post:</b> {toIT(postStart)}
                </Typography>
                <Typography variant="body2">
                  <b>Delivery deadline:</b> {toIT(deliveryDeadline)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <b>TX:</b> {txDate ? toIT(txDate) : "— (opzionale)"}
                </Typography>

                <Divider />

                <Typography variant="body2">
                  <b>Giorni lavorativi:</b> {workingDaysLabel}
                </Typography>

                <Divider />

                <Typography variant="subtitle2" sx={{ fontWeight: 900, mt: 0.5 }}>
                  Output previsto
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Fasi con durate in turni + proposta linee per gestire parallelismo. (In questa fase è un draft UI.)
                </Typography>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Mini-timeline (testuale)
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {suggestions.phases.map((ph) => (
                      <Box key={ph.name} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ph.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Math.max(1, Number(ph.turns) || 1)} turni •{" "}
                          {Math.max(1, Number(ph.lanes) || 1)} linee
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footer sticky action (wow) */}
      <Box
        sx={{
          position: "sticky",
          bottom: 16,
          mt: 3,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 900 }}>
              {isValid ? "Draft pronto" : "Completa i campi minimi"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Delivery = deadline operativa • TX = informativa
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate(`/productions/${id}`)}>
              Torna alla produzione
            </Button>
            <Button variant="contained" disabled={!isValid} onClick={handleApply}>
              Applica
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}